import { ChangeDetectionStrategy, Component, computed, effect, inject, input, linkedSignal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MarkdownViewComponent } from '../markdown-view/markdown-view.component';
import { DocumentSourceKind } from '../../utils/document-formats';

/** `pdf` is the "Original" tab, whatever the source format. */
export type DocumentViewerMode = 'pdf' | 'md';

/**
 * The single place that owns "am I looking at the original or at the markdown".
 *
 * The pipeline reads a markdown serialization of every document, not the
 * original itself, so being able to flip to that text in place is what lets an
 * engineer see what the extraction agents actually saw. Opens on the original
 * (the markdown is the debugging view), except for originals the browser cannot
 * show, which open on their markdown and are offered as a download.
 */
@Component({
  selector: 'app-document-viewer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, MarkdownViewComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './document-viewer.component.html',
  styleUrl: './document-viewer.component.scss'
})
export class DocumentViewerComponent {
  private readonly sanitizer = inject(DomSanitizer);

  /** Signed URL of the original document, whatever its format. */
  readonly pdfUrl = input<string | null>(null);
  readonly sourceKind = input<DocumentSourceKind>('pdf');
  /** Signed URL of the markdown serialization; `null` disables the toggle. */
  readonly markdownUrl = input<string | null>(null);
  readonly title = input<string>('Document Preview');
  /** Embedded uses suppress the heading row to stay compact. */
  readonly showHeader = input<boolean>(true);

  readonly mode = signal<DocumentViewerMode>('pdf');

  readonly hasMarkdown = computed(() => !!this.markdownUrl());

  readonly isImage = computed(() => this.sourceKind() === 'image');

  readonly isDownload = computed(() => this.sourceKind() === 'download');

  /** The browser could not decode the image (a TIFF before its page preview exists), so it is offered as a download. */
  readonly imageLoadFailed = linkedSignal({ source: this.pdfUrl, computation: () => false });

  readonly showsDownload = computed(() => this.isDownload() || (this.isImage() && this.imageLoadFailed()));

  readonly originalIcon = computed(() => {
    switch (this.sourceKind()) {
      case 'image':
        return 'image';
      case 'download':
        return 'insert_drive_file';
      default:
        return 'picture_as_pdf';
    }
  });

  readonly safePdfUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.pdfUrl();
    // The URL is minted by our own backend's presigner, never by user input.
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  /** Whichever URL "Open in new tab" should point at for the current mode. */
  readonly externalUrl = computed(() => (this.mode() === 'md' ? this.markdownUrl() : this.pdfUrl()));

  readonly externalLabel = computed(() =>
    this.mode() === 'md' ? 'Open markdown in new tab' : 'Open original in new tab'
  );

  constructor() {
    effect(() => {
      // Reading the inputs is the point: whenever the viewer is pointed at a
      // different document it must land on its opening tab again, rather than keep
      // a tab the reader chose for the previous one — or, worse, stay on a
      // markdown pane the new document has no file for.
      this.pdfUrl();
      const markdownUrl = this.markdownUrl();
      const sourceKind = this.sourceKind();
      this.mode.set(sourceKind === 'download' && markdownUrl ? 'md' : 'pdf');
    });
  }

  select(mode: DocumentViewerMode): void {
    if (mode === 'md' && !this.hasMarkdown()) {
      return;
    }
    this.mode.set(mode);
  }

  /** Roving focus between the two tabs, per the tablist keyboard convention. */
  onTabKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    event.preventDefault();
    const next: DocumentViewerMode = this.mode() === 'pdf' ? 'md' : 'pdf';
    if (next === 'md' && !this.hasMarkdown()) {
      return;
    }
    this.mode.set(next);

    const tablist = (event.target as HTMLElement).closest('[role="tablist"]');
    tablist?.querySelector<HTMLElement>(`[data-mode="${next}"]`)?.focus();
  }
}
